"use client";

import React, { useState, useRef, useEffect } from "react";
import { ArrowRight, ArrowUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { useIsMobile } from "@/hooks/useMediaQuery";

interface ChatComposerProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isTyping?: boolean;
  placeholder?: string;
  value?: string; // Controlled value for the input
  onValueChange?: (value: string) => void; // Callback when value changes
  onAcceptSuggestion?: () => void;
  animatedSuggestion?: { typed: string; remaining: string };
  startAction?: React.ReactNode;
  suggestions?: React.ReactNode;
  canSendWithoutMessage?: boolean;
  useBottomInset?: boolean;
  variant?: "conversation" | "entry";
}

const MAX_MESSAGE_LENGTH = 1000;

export default function ChatComposer({
  onSendMessage,
  disabled = false,
  isTyping = false,
  placeholder = "Describe your dress…",
  value: controlledValue,
  onValueChange,
  onAcceptSuggestion,
  animatedSuggestion,
  startAction,
  suggestions,
  canSendWithoutMessage = false,
  useBottomInset = true,
  variant = "conversation",
}: ChatComposerProps) {
  const [internalMessage, setInternalMessage] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wasUnavailableRef = useRef(disabled || isTyping);
  const isMobile = useIsMobile();
  const textareaMaxHeight = variant === "entry" ? 160 : isMobile ? 112 : 160;
  const textareaMinHeight = variant === "entry" ? (isMobile ? 76 : 72) : 44;

  // Use controlled value if provided, otherwise use internal state
  const message =
    controlledValue !== undefined ? controlledValue : internalMessage;
  const setMessage = (newValue: string) => {
    const nextValue = newValue.slice(0, MAX_MESSAGE_LENGTH);
    if (controlledValue !== undefined) {
      onValueChange?.(nextValue);
    } else {
      setInternalMessage(nextValue);
    }
  };

  useEffect(() => {
    // Auto-focus on mount (but not on mobile to prevent zoom)
    if (!isMobile && !disabled && !isTyping && inputRef.current) {
      inputRef.current.focus();
    }
    // The availability transition is handled separately below; this effect is
    // intentionally mount/mobile-mode only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    const unavailable = disabled || isTyping;
    const becameAvailable = wasUnavailableRef.current && !unavailable;
    wasUnavailableRef.current = unavailable;

    // The optimistic first-turn handoff mounts this composer disabled. Restore
    // keyboard focus once the designer is ready only when focus fell back to
    // the document (or Safari's focusable main container); never steal it from
    // a control the customer deliberately chose in the meantime.
    if (
      !isMobile &&
      becameAvailable &&
      inputRef.current &&
      (!document.activeElement ||
        document.activeElement === document.body ||
        document.activeElement.matches('main#main-content[tabindex="-1"]'))
    ) {
      inputRef.current.focus();
    }
  }, [disabled, isMobile, isTyping]);

  // Focus input when controlled value is set from outside.
  // Skip on mobile: programmatic focus summons the on-screen keyboard and
  // causes iOS to scroll/jank the page unexpectedly. Users can tap the input
  // explicitly on mobile.
  useEffect(() => {
    if (isMobile) return;
    if (
      controlledValue !== undefined &&
      controlledValue !== "" &&
      inputRef.current
    ) {
      inputRef.current.focus();
    }
  }, [controlledValue, isMobile]);

  // Auto-resize textarea when message changes
  useEffect(() => {
    if (inputRef.current) {
      const target = inputRef.current;
      target.style.height = "auto";
      const newHeight = Math.min(
        Math.max(target.scrollHeight, textareaMinHeight),
        textareaMaxHeight
      );
      target.style.height = `${newHeight}px`;
    }
  }, [message, textareaMaxHeight, textareaMinHeight]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      (message.trim().length === 0 && !canSendWithoutMessage) ||
      disabled ||
      isTyping
    ) {
      return;
    }

    // Validation is trim-aware, but the parent receives the exact visible
    // draft so it can restore whitespace and attachment-only input losslessly
    // if the accepted send later fails.
    onSendMessage(message.slice(0, MAX_MESSAGE_LENGTH));
    // A controlled parent may still be doing async work (for example,
    // persisting a staged reference image). Let that parent clear the draft
    // only after it accepts the turn so a failed send never erases direction.
    if (controlledValue === undefined) {
      setInternalMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === "Tab" &&
      !e.shiftKey &&
      !e.nativeEvent.isComposing &&
      message.length === 0 &&
      !disabled &&
      !isTyping &&
      onAcceptSuggestion
    ) {
      e.preventDefault();
      onAcceptSuggestion();
      return;
    }

    // Return sends on both hardware and mobile keyboards. Shift+Return keeps
    // multiline input available; an IME's confirmation Return must never send.
    if (
      !e.nativeEvent.isComposing &&
      e.nativeEvent.keyCode !== 229 &&
      e.key === "Enter" &&
      !e.shiftKey
    ) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const canSend =
    (message.trim().length > 0 || canSendWithoutMessage) &&
    !disabled &&
    !isTyping;
  const showAnimatedSuggestion =
    message.length === 0 && animatedSuggestion !== undefined;
  const remainingChars = Math.max(0, MAX_MESSAGE_LENGTH - message.length);

  const attachmentAction = startAction ? (
    <div
      data-slot="chat-composer-start"
      className="flex size-11 shrink-0 items-center"
    >
      {startAction}
    </div>
  ) : null;

  return (
    // Input area - normally pinned to the bottom of the chat shell by layout
    // (not absolute positioning). Bottom-anchored instances combine:
    // - `env(safe-area-inset-bottom)` for the iPhone home indicator
    // - `var(--keyboard-inset)` from useVirtualKeyboardInset so the
    //   composer lifts above the iOS on-screen keyboard (Android is
    //   handled via `interactive-widget=resizes-content`).
    // A single background surface — the chat-frame band matches the header
    // bar so the transcript sits framed between them.
    <div
      data-slot="chat-composer"
      data-variant={variant}
      className="shrink-0 border-t border-border bg-chat-frame"
      style={{
        paddingBottom: useBottomInset
          ? "max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px))"
          : "0px",
      }}
    >
      <div className="flex w-full min-w-0 shrink-0 flex-col">
        {/* Form – padding aligned with CardHeader bar */}
        <div
          data-slot="chat-composer-inner"
          className="mx-auto w-full max-w-4xl space-y-2 px-4 py-3 sm:px-6"
        >
          <div className="flex min-w-0 items-end gap-2">
            <form
              data-slot="chat-composer-form"
              onSubmit={handleSubmit}
              className="flex min-w-0 flex-1 items-end gap-2 rounded-2xl border border-transparent bg-background py-[5px] pr-1.5 pl-3 shadow-sm"
            >
              <div data-slot="chat-composer-field" className="contents">
                {variant === "conversation" ? attachmentAction : null}
                <div
                  data-slot="chat-composer-input"
                  className="relative min-w-0 flex-1"
                >
                  {showAnimatedSuggestion ? (
                    <span
                      data-slot="chat-composer-ghost"
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-0 top-0 whitespace-pre-wrap break-words px-1 py-2 text-base leading-6 text-muted-foreground"
                    >
                      Try:{" "}
                      <span
                        data-slot="chat-composer-ghost-typed"
                        className="text-foreground/80"
                      >
                        {animatedSuggestion?.typed}
                      </span>
                      <span>{animatedSuggestion?.remaining}</span>
                    </span>
                  ) : null}
                  <Textarea
                    ref={inputRef}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={showAnimatedSuggestion ? "" : placeholder}
                    disabled={disabled}
                    maxLength={MAX_MESSAGE_LENGTH}
                    rows={1}
                    enterKeyHint="send"
                    aria-label="Chat message input"
                    aria-description={
                      onAcceptSuggestion
                        ? animatedSuggestion
                          ? `Suggested idea: ${animatedSuggestion.typed}${animatedSuggestion.remaining}. Press Tab to use it, or type your own.`
                          : "Press Tab to use the suggested idea, or type your own."
                        : undefined
                    }
                    // The pill shows focus only while this textarea is focused.
                    // Its action buttons keep their own focus indicators.
                    className="resize-none border-0 bg-transparent dark:bg-transparent px-0 py-2.5 shadow-none [--focus-ring:none] focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
                    style={{
                      minHeight: `${textareaMinHeight}px`,
                      maxHeight: `${textareaMaxHeight}px`,
                      height: `${textareaMinHeight}px`,
                    }}
                    onInput={e => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      const newHeight = Math.min(
                        Math.max(target.scrollHeight, textareaMinHeight),
                        textareaMaxHeight
                      );
                      target.style.height = `${newHeight}px`;
                    }}
                  />
                </div>
                {variant === "entry" ? attachmentAction : null}
                {variant === "entry" && suggestions ? (
                  <div data-slot="chat-composer-suggestions">{suggestions}</div>
                ) : null}
              </div>

              <Button
                data-slot="chat-composer-submit"
                type="submit"
                variant="default"
                disabled={!canSend}
                aria-label={
                  variant === "entry" ? "Start designing" : "Send message"
                }
                title={
                  !canSend && !message.trim() && !canSendWithoutMessage
                    ? "Type to continue"
                    : undefined
                }
                size={variant === "entry" ? "default" : "icon-lg"}
                className={`min-h-11 shrink-0 touch-manipulation gap-2 rounded-xl ${variant === "conversation" ? "w-11" : ""}`}
              >
                {variant === "entry" ? <span>Start designing</span> : null}
                {variant === "entry" ? (
                  <ArrowRight className="size-4" aria-hidden />
                ) : (
                  <ArrowUp className="size-4" aria-hidden />
                )}
              </Button>
            </form>
          </div>

          {variant === "conversation" && suggestions ? (
            <div data-slot="chat-composer-suggestions">{suggestions}</div>
          ) : null}

          {remainingChars < 100 && (
            <div className="flex justify-end">
              <Text
                as="span"
                variant="default"
                size="xs"
                className="text-foreground/80"
              >
                {remainingChars} characters left
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
